<script setup>
import { useTrackPageView } from '~/composables/gtm/useTrackPageView'
import { useCanonical } from '~/composables/seo/canonicals'
import { useHrefLang } from '~/composables/seo/hreflang'
import {
  getOgDescription,
  getOgTitle,
  useOpenGraph,
} from '~/composables/seo/opengraph'
import { useRobotsMeta } from '~/composables/seo/robotsMeta'
import {
  getFAQQuestions,
  useStructuredFAQ,
} from '~/composables/structuredData/faq'
import { useStructuredLogo } from '~/composables/structuredData/logo'
import { useStructuredVideo } from '~/composables/structuredData/video'

// pages
import Index from '~/pages/base.vue'
import ThankYou from '~/pages/checkout/ThankYou.vue'
import Video from '~/pages/cms/Video.vue'
import Course from '~/pages/course/Course.vue'
import CourseOverview from '~/pages/course/Overview.vue'
import JobPostingOverview from '~/pages/job/Overview.vue'
import JobPosting from '~/pages/job/Posting.vue'
import LocationDetail from '~/pages/location/Detail.vue'
import LocationOverview from '~/pages/location/Overview.vue'
import MagazineCategory from '~/pages/magazine/Category.vue'
import MagazineContent from '~/pages/magazine/Item.vue'
import MagazineOverview from '~/pages/magazine/Overview.vue'
import Catalogue from '~/pages/product/Catalogue.vue'
import ProductCategory from '~/pages/product/Category.vue'
import ProductDetail from '~/pages/product/Detail.vue'
import Employee from '~/pages/team/Employee.vue'
import Team from '~/pages/team/Team.vue'

const { t, locale } = useI18n()
const img = useImage()
const route = useRoute()
const wagtail = useWagtailClient()
const i18nStore = useI18NStore()
const authStore = useAuthStore()
const seoStore = useSeoStore()
const { trackPageView } = useTrackPageView()

const openGraphImageUrl = ref('')

const pagePath = (route.path || '/').replace(/\/$/, '') || '/'

const { data: page, error } = await useAsyncData(`page${pagePath}`, async () => {
  const res = await wagtail.getPageByPath(pagePath)

  if (res) {
    return res.data
  }
})

if (!page?.value && error.value) {
  throw error.value
}

useHead({
  title: page?.value?.meta?.seo_title
    ? page?.value?.meta?.seo_title
    : page?.value
      ? `${page?.value?.title} | LUMA Delikatessen`
      : `404 - ${t('error.404.title')}`,
  meta: [
    {
      hid: 'description',
      name: 'description',
      content: cleanMetaString(page?.value?.meta?.search_description),
    },
  ],
})

const pageMapping = {
  'cms.EventCourseOverviewPage': CourseOverview,
  'cms.CoursePage': Course,
  'cms.TeamPage': Team,
  'cms.EmployeePage': Employee,
  'cms.LocationOverviewPage': LocationOverview,
  'cms.LocationPage': LocationDetail,
  'cms.JobPostingOverviewPage': JobPostingOverview,
  'cms.JobPostingPage': JobPosting,
  'cms.VideoCmsPage': Video,
  'cms.LumaBasePage': Index,
  'cms.MagazinePage': MagazineOverview,
  'cms.MagazineCategoryPage': MagazineCategory,
  'cms.MagazineContentPage': MagazineContent,
  'cms.MagazineTagPage': MagazineCategory,
  'cms.CataloguePage': Catalogue,
  'cms.CategoryPage': ProductCategory,
  'cms.ProductPage': ProductDetail,
  'cms.ThankYouPage': ThankYou,
}

const pageType = computed(() => {
  return page?.value?.meta?.type
})

const pageComponent = computed(() => {
  return pageMapping[pageType.value]
})

useHead({
  htmlAttrs: {
    lang: locale.value,
  },
})

useHrefLang(page?.value?.meta?.hreflang)
useStructuredLogo()

if (page?.value?.meta?.type !== 'cms.EmployeePage') {
  openGraphImageUrl.value = img(page?.value?.open_graph_image_url, {
    width: 1200,
    height: 630,
    format: 'jpeg',
  })
}

useOpenGraph(
  getOgTitle(page),
  getOgDescription(page),
  openGraphImageUrl.value,
  page?.value?.meta?.html_url,
)

useStructuredVideo(page?.value)

const faq = getFAQQuestions(page)
useStructuredFAQ(faq)

if (page?.value?.meta?.hreflang) {
  i18nStore.hreflang = page?.value?.meta?.hreflang
}

useCanonical()

if (page?.value?.meta?.type === 'cms.ProductPage') {
  let product = page?.value?.product
  let price = null

  if (page?.value?.product?.structure === 'standalone') {
    price = page?.value?.product?.price
  }
  else {
    const variant = route.query.variant

    if (variant) {
      for (const child of page?.value?.product?.children) {
        if (child.id === Number.parseInt(variant)) {
          product = child
          price = child.price
          break
        }
      }
      if (!price) {
        // Product variant does not exist - redirect
        navigateTo(`${route.path}`)
      }
    }
    else {
      product = page?.value?.product?.children.find(child => child.availability.is_available_to_buy)

      if (product) {
        price = product.price
      }
      else {
        product = page?.value?.product?.children[0]
        price = product.price
      }
    }
  }

  trackPageView(
    page?.value?.meta?.type,
    page?.value,
    authStore?.user?.id,
    price,
    product,
  )

  if (route.query.variant) {
    useRobotsMeta('noindex,follow')
  }
  else {
    useRobotsMeta(page?.value?.robots_meta_tag)
  }
}
else {
  useRobotsMeta(page?.value?.robots_meta_tag)
  trackPageView(page?.value?.meta?.type, page?.value, authStore?.user?.id)
}

onMounted(() => {
  seoStore.pageType = page?.value?.meta?.type
  seoStore.pageTitle = page?.value?.title
})
</script>

<template>
  <component
    :is="pageComponent"
    v-if="pageComponent"
    :page-obj="page"
  />
</template>

<style lang="scss"></style>
